import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, PartyPopper } from "lucide-react";
import { Switch } from "@/components/ui-kit";
import { EventTypeDialog } from "@/components/EventTypeDialog";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import type { EventType } from "@/types";

export const EventTypesManager = ({ canEdit }: { canEdit: boolean }) => {
  const { t, lang } = usePrefs();
  const [types, setTypes] = useState<(EventType & { events_count?: number })[]>([]);
  const [dlg, setDlg] = useState<{ open: boolean; type: EventType | null }>({ open: false, type: null });
  const load = () => api.get("/admin/event-types").then((r) => setTypes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async (fn: () => Promise<unknown>) => {
    try { await fn(); toast.success(t("admin.done")); load(); } catch (e) { toast.error(errorText(e, t)); }
  };
  const remove = (x: EventType & { events_count?: number }) => {
    if (x.events_count) return toast.error(t("et.inUse").replace("{n}", String(x.events_count)));
    if (window.confirm(t("et.confirmDelete").replace("{name}", lang === "ar" ? x.name_ar : x.name_en))) run(() => api.delete(`/admin/event-types/${x.slug}`));
  };

  return (
    <div className="rounded-3xl border bg-card p-5 space-y-4" data-testid="event-types-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex gap-3">
          <PartyPopper className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div><p className="font-bold">{t("ae.types")}</p><p className="text-sm text-muted-foreground">{t(canEdit ? "et.sub" : "et.readOnly")}</p></div>
        </div>
        {canEdit && <button onClick={() => setDlg({ open: true, type: null })} data-testid="event-type-new" className="h-10 px-4 rounded-full bg-primary text-primary-foreground text-sm font-bold inline-flex items-center gap-1.5 shrink-0"><Plus className="h-4 w-4" />{t("ae.newType")}</button>}
      </div>
      <ul className="divide-y">
        {types.map((x) => (
          <li key={x.slug} data-testid={`event-type-${x.slug}`} className="flex items-center gap-3 py-2.5">
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{lang === "ar" ? x.name_ar : x.name_en}</p>
              <p className="text-xs text-muted-foreground" data-testid={`event-type-count-${x.slug}`}>{lang === "ar" ? x.name_en : x.name_ar} · {t(lang === "ar" && (x.events_count || 0) > 2 && (x.events_count || 0) < 11 ? "et.countFew" : "et.count").replace("{n}", String(x.events_count || 0))}</p>
            </div>
            <Switch checked={x.active} disabled={!canEdit} onCheckedChange={(v: boolean) => run(() => api.patch(`/admin/event-types/${x.slug}`, { active: v }))} data-testid={`event-type-toggle-${x.slug}`} aria-label={t(x.active ? "ae.active" : "ae.inactive")} />
            {canEdit && (
              <div className="flex gap-1">
                <button onClick={() => setDlg({ open: true, type: x })} aria-label={t("et.edit")} data-testid={`event-type-edit-${x.slug}`} className="h-9 w-9 grid place-items-center rounded-full border hover:bg-accent"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(x)} aria-label={t("et.delete")} data-testid={`event-type-delete-${x.slug}`} className={`h-9 w-9 grid place-items-center rounded-full border ${x.events_count ? "opacity-40" : "hover:bg-destructive/10 text-destructive"}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <EventTypeDialog open={dlg.open} type={dlg.type} onClose={() => setDlg({ open: false, type: null })} onSaved={load} />
    </div>
  );
};
